import { useState, useEffect } from 'react';
import API from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Users, Building2, Phone, Mail, Clock, Calendar, Search, QrCode, Loader2, FileText, TrendingUp, Copy, Download } from 'lucide-react';
import { toast } from 'sonner';

export default function MRVisitorsPage() {
  const [visitors, setVisitors] = useState([]);
  const [todayVisitors, setTodayVisitors] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchCompany, setSearchCompany] = useState('');
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [showQR, setShowQR] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [visitorsRes, todayRes, statsRes] = await Promise.all([
        API.get('/mr-checkin/my-visitors'),
        API.get('/mr-checkin/my-visitors/today'),
        API.get('/mr-checkin/my-visitors/stats')
      ]);
      setVisitors(visitorsRes.data);
      setTodayVisitors(todayRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!selectedVisitor) return;
    setSaving(true);
    try {
      await API.put(`/mr-checkin/my-visitors/${selectedVisitor.id}/notes`, null, {
        params: { notes }
      });
      toast.success('Notes saved');
      setSelectedVisitor(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to save notes');
    } finally {
      setSaving(false);
    }
  };

  const openVisitorDetails = (visitor) => {
    setSelectedVisitor(visitor);
    setNotes(visitor.notes || '');
  };

  const getKioskUrl = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return `${window.location.origin}/mr-kiosk/${user.id || 'clinic'}`;
  };

  const copyKioskUrl = () => {
    navigator.clipboard.writeText(getKioskUrl());
    toast.success('Kiosk URL copied to clipboard');
  };

  const filteredVisitors = visitors.filter(v => 
    !searchCompany || v.company_name?.toLowerCase().includes(searchCompany.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="mr-visitors-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>MR Visitors</h1>
          <p className="text-sm text-slate-500 mt-1">Track Medical Representative visits to your clinic</p>
        </div>
        <Button onClick={() => setShowQR(true)} variant="outline" data-testid="show-mr-qr">
          <QrCode className="w-4 h-4 mr-2" /> Kiosk QR Code
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-purple-200 bg-purple-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <Calendar className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-700">{stats.today || 0}</p>
                <p className="text-sm text-purple-600">Today</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-teal-200 bg-teal-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-teal-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-teal-700">{stats.this_month || 0}</p>
                <p className="text-sm text-teal-600">This Month</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-700">{stats.total || 0}</p>
                <p className="text-sm text-slate-600">Total Visits</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div>
              <p className="text-sm text-amber-600 mb-2">Top Companies</p>
              <div className="space-y-1">
                {(stats.top_companies || []).slice(0, 3).map((c, i) => (
                  <div key={`el-${i}`} className="flex items-center justify-between text-sm">
                    <span className="text-amber-800 truncate">{c.company}</span>
                    <Badge variant="secondary" className="bg-amber-100">{c.visits}</Badge>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Today's Visitors */}
      {todayVisitors.length > 0 && (
        <Card className="border-green-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2 text-green-700">
              <Clock className="w-5 h-5" /> Today's Visitors ({todayVisitors.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {todayVisitors.map((visitor) => (
                <div
                  key={visitor.id}
                  className="flex items-center justify-between p-3 bg-green-50 rounded-lg cursor-pointer hover:bg-green-100 transition-colors"
                  onClick={() => openVisitorDetails(visitor)}
                  data-testid={`today-visitor-${visitor.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-200 rounded-full flex items-center justify-center">
                      <span className="font-bold text-green-700">{visitor.mr_name?.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{visitor.mr_name}</p>
                      <p className="text-sm text-slate-500">{visitor.company_name}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-green-700">
                      {new Date(visitor.check_in_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                    {visitor.check_out_time ? (
                      <Badge className="bg-slate-100 text-slate-600">Checked Out</Badge>
                    ) : (
                      <Badge className="bg-green-100 text-green-700">Active</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* All Visitors */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-teal-600" /> All Visitors
              </CardTitle>
              <CardDescription>Complete visitor history</CardDescription>
            </div>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by company..."
                value={searchCompany}
                onChange={(e) => setSearchCompany(e.target.value)}
                className="pl-10"
                data-testid="mr-visitor-search"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredVisitors.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No visitors yet</p>
              <p className="text-sm text-slate-400 mt-1">Share your kiosk QR code with MRs</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">MR Name</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Company</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Purpose</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Check-In</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredVisitors.map((visitor) => (
                    <tr
                      key={visitor.id}
                      className="border-b hover:bg-slate-50 cursor-pointer"
                      onClick={() => openVisitorDetails(visitor)}
                      data-testid={`visitor-row-${visitor.id}`}
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                            <span className="font-bold text-purple-700 text-sm">{visitor.mr_name?.charAt(0)}</span>
                          </div>
                          <div>
                            <p className="font-medium">{visitor.mr_name}</p>
                            <p className="text-xs text-slate-400">{visitor.phone}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">{visitor.company_name}</td>
                      <td className="py-3 px-4">
                        <Badge variant="outline" className="capitalize">
                          {visitor.purpose?.replace('_', ' ') || 'N/A'}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-500">
                        {new Date(visitor.check_in_time).toLocaleDateString()}<br />
                        <span className="text-xs">{new Date(visitor.check_in_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      </td>
                      <td className="py-3 px-4">
                        {visitor.check_out_time ? (
                          <Badge className="bg-slate-100 text-slate-600">Completed</Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-700">Active</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Visitor Details Dialog */}
      <Dialog open={!!selectedVisitor} onOpenChange={() => setSelectedVisitor(null)}>
        <DialogContent className="max-w-lg">
          {selectedVisitor && (
            <>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope' }}>Visitor Details</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex items-center gap-4 pb-4 border-b">
                  <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center">
                    <span className="font-bold text-purple-700 text-xl">{selectedVisitor.mr_name?.charAt(0)}</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">{selectedVisitor.mr_name}</h3>
                    <p className="text-slate-500">{selectedVisitor.company_name}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span>{selectedVisitor.phone || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <span>{selectedVisitor.email || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-slate-400" />
                    <span>In: {new Date(selectedVisitor.check_in_time).toLocaleString()}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-slate-400" />
                    <span>Out: {selectedVisitor.check_out_time ? new Date(selectedVisitor.check_out_time).toLocaleString() : 'Not yet'}</span>
                  </div>
                </div>

                <div>
                  <p className="text-sm font-medium text-slate-700 mb-1">Purpose</p>
                  <Badge variant="outline" className="capitalize">{selectedVisitor.purpose?.replace('_', ' ') || 'Not specified'}</Badge>
                </div>

                {selectedVisitor.products_discussed?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-slate-700 mb-2">Products Discussed</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedVisitor.products_discussed.map((p, i) => (
                        <Badge key={`badge-${i}`} variant="secondary">{p}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <p className="text-sm font-medium text-slate-700 mb-2">Notes</p>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add notes about this visit..."
                    rows={3}
                    data-testid="visitor-notes"
                  />
                </div>

                <Button onClick={handleSaveNotes} className="w-full" disabled={saving} data-testid="save-visitor-notes">
                  {saving ? 'Saving...' : 'Save Notes'}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={showQR} onOpenChange={setShowQR}>
        <DialogContent className="max-w-md text-center">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>MR Check-In Kiosk</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-white p-6 rounded-lg border mb-4 flex flex-col items-center justify-center">
              {/* Generate QR Code using API */}
              <img 
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(getKioskUrl())}`}
                alt="MR Kiosk QR Code"
                className="w-48 h-48"
              />
              <p className="text-xs text-slate-500 mt-3">Scan to open MR Check-In</p>
            </div>
            <p className="text-sm text-slate-500 mb-4">Share this link with Medical Representatives for self check-in:</p>
            <div className="flex items-center gap-2 bg-slate-50 p-3 rounded-lg">
              <Input value={getKioskUrl()} readOnly className="text-sm" />
              <Button variant="outline" size="icon" onClick={copyKioskUrl} data-testid="copy-kiosk-url">
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <div className="mt-4 flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => window.open(getKioskUrl(), '_blank')}>
                Open Kiosk Page
              </Button>
              <Button variant="outline" className="flex-1" onClick={() => {
                const link = document.createElement('a');
                link.href = `https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(getKioskUrl())}`;
                link.download = 'mr-kiosk-qr.png';
                link.click();
              }}>
                <Download className="w-4 h-4 mr-2" /> Download QR
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
